import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { FileText, TrendingUp, CalendarDays, CalendarClock, CalendarRange, Plus, X, Pencil, Trash2, Hash, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import InvoicePDFButton from "../../components/InvoicePDFButton";
import { rank } from "../../lib/rbac";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditInvoiceModal = ({ onClose, onEdit, invoice }) => {
  const [formData, setFormData] = useState({ invoice_number: invoice.id, ...invoice });

  const handleSubmit = (e) => {
    e.preventDefault();
    onEdit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-1">Edit Invoice</h2>
        <p className="text-xs text-text_secondary mb-6">CEO access — every field including the invoice number and date is editable.</p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Invoice Number</label>
            <input required data-testid="edit-invoice-number" type="text" value={formData.invoice_number} onChange={e=>setFormData({...formData, invoice_number: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary font-mono focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Member / Customer</label>
            <input required type="text" value={formData.member} onChange={e=>setFormData({...formData, member: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Description</label>
            <input required type="text" value={formData.type} onChange={e=>setFormData({...formData, type: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Value (₹)</label>
              <input required type="number" min="0" value={formData.amount} onChange={e=>setFormData({...formData, amount: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Date</label>
              <input required data-testid="edit-invoice-date" type="date" value={formData.date} onChange={e=>setFormData({...formData, date: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Payment Mode</label>
              <select value={formData.mode || "Cash"} onChange={e=>setFormData({...formData, mode: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                {["Cash","UPI","Card","Net Banking"].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Status</label>
              <select required value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option>Paid</option>
                <option>Pending</option>
                <option>Cancelled</option>
              </select>
            </div>
          </div>
          <button type="submit" data-testid="save-invoice-edit-btn" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Edits</button>
        </form>
      </div>
    </div>
  );
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [user, setUser] = useState(null);
  
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [customData, setCustomData] = useState({ member: "", item_description: "", amount: 0, status: "Paid", mode: "Cash" });

  const fetchInvoices = useCallback(async () => {
    try {
      const [res, me] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/invoices`),
        axios.get(`${BACKEND_URL}/api/auth/me`),
      ]);
      setInvoices(res.data);
      setUser(me.data.user);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const isCEO = user && rank(user.role) >= 100;
  const canBackfill = user && rank(user.role) >= 90;

  const todayStr = new Date().toISOString().split('T')[0];
  const todayTotal = invoices.filter(i => i.date === todayStr && i.status === "Paid").reduce((acc, curr) => acc + curr.amount, 0);
  const pendingDues = invoices.filter(i => i.status && i.status !== "Paid" && i.status !== "Cancelled").reduce((acc, curr) => acc + curr.amount, 0);

  const barData = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const shortName = d.toLocaleDateString('en-US', { weekday: 'short' });
      
      const total = invoices
        .filter(inv => inv.date === dateStr && inv.status === "Paid")
        .reduce((sum, inv) => sum + inv.amount, 0);
        
      days.push({ id: i, name: shortName, val: total });
    }
    return days;
  }, [invoices]);

  const cartesianConfig = useMemo(() => ({ strokeDasharray: "3 3", stroke: "rgba(197,160,89,0.05)", vertical: false }), []);
  const tooltipConfig = useMemo(() => ({ cursor: {fill: 'rgba(255,255,255,0.03)'}, contentStyle: { backgroundColor: '#091A0F', border: '1px solid rgba(197,160,89,0.3)', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', color: '#c5a059', fontWeight: 'bold' } }), []);
  const barConfig = useMemo(() => ({ fill: "#c5a059", radius: [0, 6, 6, 0], barSize: 30 }), []);

  const handleCustomInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/custom`, customData);
      setShowCustomModal(false);
      setCustomData({ member: "", item_description: "", amount: 0, status: "Paid", mode: "Cash" });
      fetchInvoices();
    } catch(e) { console.error(e); }
  };

  const markPaid = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${id}/pay`);
      fetchInvoices();
    } catch(e) { console.error(e); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/invoices/${data.id}`, {
        invoice_number: data.invoice_number,
        member: data.member, amount: data.amount, type: data.type,
        status: data.status, mode: data.mode, date: data.date,
      });
      setShowEdit(null);
      fetchInvoices();
    } catch(e) { alert(e.response?.data?.detail || "Only the CEO can edit invoices."); }
  };

  const handleBackfill = async () => {
    if (!window.confirm("Generate invoices for any members that don't have one yet? This uses each member's package price.")) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/backfill-invoices`);
      alert(`Done. ${res.data.created} invoice(s) generated.`);
      fetchInvoices();
    } catch(e) { alert(e.response?.data?.detail || "Only CEO or MD can run this."); }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Delete this invoice?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/invoices/${id}`);
        fetchInvoices();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
    }
  };

  const downloadReport = (type) => {
    const doc = new jsPDF();
    let filteredInvoices = [];
    let reportTitle = "";

    if (type === "Daily") {
      reportTitle = `Daily Sales Report (${todayStr})`;
      filteredInvoices = invoices.filter(i => i.date === todayStr);
    } else if (type === "Monthly") {
      const monthStr = todayStr.substring(0, 7); // YYYY-MM
      reportTitle = `Monthly Sales Report (${monthStr})`;
      filteredInvoices = invoices.filter(i => i.date.startsWith(monthStr));
    } else if (type === "Yearly") {
      const yearStr = todayStr.substring(0, 4); // YYYY
      reportTitle = `Yearly Sales Report (${yearStr})`;
      filteredInvoices = invoices.filter(i => i.date.startsWith(yearStr));
    }

    doc.setFontSize(20);
    doc.setTextColor(5, 20, 13);
    doc.text("SVT GOLDEN FITNESS", 14, 22);
    
    doc.setFontSize(14);
    doc.setTextColor(100, 100, 100);
    doc.text(reportTitle, 14, 30);

    const totalRevenue = filteredInvoices.reduce((sum, inv) => sum + (inv.status === 'Paid' ? inv.amount : 0), 0);
    doc.setFontSize(12);
    doc.setTextColor(197, 160, 89);
    doc.text(`Total Paid Revenue: INR ${totalRevenue.toLocaleString()}`, 14, 38);

    const tableColumn = ["Invoice #", "Date", "Member", "Type", "Status", "Amount"];
    const tableRows = [];

    filteredInvoices.forEach(inv => {
      const rowData = [ inv.id, inv.date, inv.member, inv.type, inv.status, `Rs. ${inv.amount.toLocaleString()}` ];
      tableRows.push(rowData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 45,
      theme: 'grid',
      headStyles: { fillColor: [5, 20, 13], textColor: [212, 175, 55] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`${reportTitle.replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 relative">
      <div className="flex flex-col xl:flex-row justify-between xl:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Billing</h1>
          <p className="text-text_secondary text-sm">View payments, create invoices and track dues.</p>
        </div>
        
        <div className="flex gap-3 flex-wrap">
          {canBackfill && (
            <button data-testid="backfill-invoices-btn" onClick={handleBackfill} className="bg-card_bg border border-border_soft text-text_primary px-5 py-3 rounded-xl font-bold hover:border-gold transition-colors flex items-center gap-2 text-sm">
              <RefreshCw size={16} className="text-gold" /> Generate Missing Invoices
            </button>
          )}
          <button onClick={() => setShowCustomModal(true)} className="bg-primary/10 border border-primary/30 text-gold px-5 py-3 rounded-xl font-bold hover:bg-primary/20 transition-all flex items-center gap-2 shadow-glow text-sm">
            <Plus size={16} strokeWidth={2.5} /> Add Invoice
          </button>
          <div className="h-10 w-px bg-border_gold/50 self-center hidden sm:block"></div>
          <button onClick={() => downloadReport('Daily')} className="bg-card_bg border border-border_soft text-text_primary px-5 py-3 rounded-xl font-bold hover:bg-card_bg/10 transition-colors flex items-center gap-2 text-sm">
            <CalendarDays size={16} className="text-text_secondary"/> Daily Report
          </button>
          <button onClick={() => downloadReport('Monthly')} className="bg-card_bg border border-border_soft text-text_primary px-5 py-3 rounded-xl font-bold hover:bg-card_bg/10 transition-colors flex items-center gap-2 text-sm">
            <CalendarRange size={16} className="text-text_secondary"/> Monthly Report
          </button>
          <button onClick={() => downloadReport('Yearly')} className="bg-card_bg border border-border_soft text-text_primary px-5 py-3 rounded-xl font-bold hover:bg-card_bg/10 transition-colors flex items-center gap-2 text-sm">
            <CalendarClock size={16} className="text-text_secondary"/> Yearly Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <div className="bg-card_bg border border-border_soft p-8 rounded-2xl shadow-sm flex flex-col justify-center relative overflow-hidden group">
          <div className="absolute -right-4 -top-4 w-32 h-32 bg-primary/5 rounded-full blur-2xl"></div>
          <div className="flex items-center gap-3 mb-4 text-text_secondary">
            <div className="bg-primary/10 p-2.5 rounded-xl"><TrendingUp size={24} className="text-gold"/></div>
            <span className="text-sm font-bold uppercase tracking-widest">Today's Revenue</span>
          </div>
          <h2 className="text-5xl font-bold text-text_primary tracking-tight mt-2">₹{todayTotal.toLocaleString()}</h2>
          <p className="text-sm mt-3 text-text_secondary">Pending dues: <span className="text-accent_danger font-bold" data-testid="pending-dues-value">₹{pendingDues.toLocaleString()}</span></p>
        </div>

        <div className="xl:col-span-2 bg-card_bg border border-border_soft p-8 rounded-2xl shadow-sm h-56 flex flex-col">
          <h3 className="text-sm font-bold text-gold mb-6 uppercase tracking-widest flex items-center gap-2">
            <FileText size={16}/> Revenue (Last 7 Days)
          </h3>
          <div className="flex-1 w-full min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid {...cartesianConfig} />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={12} tickLine={false} axisLine={false} dy={10} />
                <Tooltip cursor={tooltipConfig.cursor} contentStyle={tooltipConfig.contentStyle} itemStyle={{color: '#c5a059', fontWeight: 'bold'}} />
                <Bar dataKey="val" fill={barConfig.fill} radius={barConfig.radius} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-card_bg border border-border_soft rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead className="bg-card_bg/80 backdrop-blur-sm border-b border-border_soft/50">
              <tr>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Invoice #</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Details</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Date</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Status</th>
                <th className="px-8 py-5 text-[10px] uppercase tracking-widest text-text_secondary font-bold text-right">Amount</th>
                <th className="px-8 py-5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border_gold/20">
              {invoices.length === 0 && <tr><td colSpan="6" className="px-8 py-8 text-center text-text_secondary italic">No invoices yet.</td></tr>}
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-card_bg/30 transition-colors group">
                  <td className="px-8 py-5 text-text_secondary/80 font-mono text-sm flex items-center gap-2"><Hash size={14} className="opacity-50"/>{inv.id}</td>
                  <td className="px-8 py-5">
                    <p className="text-text_primary font-bold text-sm mb-1">{inv.member}</p>
                    <p className="text-xs text-text_secondary/70 truncate max-w-xs" title={inv.type}>{inv.type}</p>
                  </td>
                  <td className="px-8 py-5 text-text_secondary text-sm font-mono">{inv.date}</td>
                  <td className="px-8 py-5">
                    <span className={`px-3 py-1.5 text-[10px] uppercase font-bold tracking-widest rounded-lg border ${inv.status==='Paid' ? 'bg-accent_success/10 border-accent_success/20 text-accent_success' : inv.status==='Pending' ? 'bg-primary/10 border-primary/20 text-gold' : 'bg-accent_danger/10 border-accent_danger/20 text-accent_danger'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-text_primary font-bold text-right tracking-wide text-lg">₹{inv.amount.toLocaleString()}</td>
                  <td className="px-8 py-5">
                    <div className="flex justify-end items-center gap-3 opacity-0 md:opacity-0 group-hover:opacity-100 transition-opacity bg-card_bg/80 backdrop-blur-sm p-1.5 rounded-lg border border-border_soft/50 ml-auto w-fit">
                      {inv.status !== 'Paid' && inv.status !== 'Cancelled' && <button data-testid="mark-paid-btn" onClick={() => markPaid(inv.id)} className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-accent_success/15 text-accent_success border border-accent_success/30 rounded-md hover:bg-accent_success/25 transition-colors" title="Mark as Paid">Mark Paid</button>}
                      {isCEO && <button data-testid="edit-invoice-btn" onClick={() => setShowEdit(inv)} className="p-1.5 text-text_secondary hover:text-gold transition-colors" title="Edit (CEO)"><Pencil size={16}/></button>}
                      {isCEO && <button onClick={() => handleDelete(inv.id)} className="p-1.5 text-text_secondary hover:text-accent_danger transition-colors" title="Delete"><Trash2 size={16}/></button>}
                      <InvoicePDFButton invoice={inv} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showEdit && <EditInvoiceModal invoice={showEdit} onClose={() => setShowEdit(null)} onEdit={handleEdit} />}

      {/* Custom Invoice Modal */}
      {showCustomModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl">
            <button onClick={() => setShowCustomModal(false)} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
            <h2 className="text-2xl font-serif text-text_primary mb-6">Add Invoice</h2>
            <form onSubmit={handleCustomInvoice} className="space-y-5">
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Member / Customer Name</label>
                <input required type="text" value={customData.member} onChange={e=>setCustomData({...customData, member: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Description</label>
                <input required type="text" value={customData.item_description} onChange={e=>setCustomData({...customData, item_description: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Amount (₹)</label>
                <input required type="number" min="0" value={customData.amount} onChange={e=>setCustomData({...customData, amount: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Payment Mode</label>
                  <select data-testid="invoice-mode" value={customData.mode} onChange={e=>setCustomData({...customData, mode: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                    {["Cash","UPI","Card","Net Banking"].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Status</label>
                  <select data-testid="invoice-status" value={customData.status} onChange={e=>setCustomData({...customData, status: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                    <option>Paid</option>
                    <option>Pending</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Invoice</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
